import z from 'zod';
import { ConnectResourceType } from '../api/api-entities';
import { validatePrimaryField } from './validation';

export enum SchemaFieldType {
  STRING = 'string',
  NUMBER = 'number',
  BOOLEAN = 'boolean',
  REFERENCE = 'reference',
  OBJECT = 'object',
  YAML = 'yaml',
  PROMPT = 'prompt',
  PICK_LIST = 'picklist',
  DATE_TIME = 'date_time',
  CONNECT_RESOURCE = 'connect_resource'
}

export const StringSchema = z
  .object({
    type: z.literal(SchemaFieldType.STRING),
    minLength: z.number().min(0),
    maxLength: z.number().min(0),
    required: z.boolean()
  })
  .refine(
    (data) => {
      return data.minLength <= data.maxLength;
    },
    {
      message: 'minLength must be less than or equal to maxLength'
    }
  )
  .refine(
    (data) => {
      if (!data.required) {
        return true;
      }

      return data.minLength > 0 && data.maxLength > 0;
    },
    {
      message: 'Required string fields must have positive minLength and maxLength.'
    }
  );

export const NumberSchema = z
  .object({
    type: z.literal(SchemaFieldType.NUMBER),
    minimum: z.number(),
    maximum: z.number(),
    required: z.boolean()
  })
  .refine(
    (data) => {
      return data.minimum <= data.maximum;
    },
    {
      message: 'minimum must be less than or equal to maximum'
    }
  );

export const BooleanSchema = z.object({
  type: z.literal(SchemaFieldType.BOOLEAN),
  required: z.boolean()
});

export const PromptSchema = z.object({
  type: z.literal(SchemaFieldType.PROMPT),
  required: z.boolean()
});

export const YamlSchema = z.object({
  type: z.literal(SchemaFieldType.YAML),
  required: z.boolean()
});

export const PickListSchema = z.object({
  type: z.literal(SchemaFieldType.PICK_LIST),
  options: z.array(z.string().min(1).max(128)).min(1).max(128),
  multiple: z.boolean(),
  required: z.boolean()
});

export const ReferenceSchema = z.object({
  type: z.literal(SchemaFieldType.REFERENCE),
  entityId: z.string().min(1).max(64),
  required: z.boolean()
});

export const ObjectSchema = z.object({
  type: z.literal(SchemaFieldType.OBJECT),
  required: z.boolean()
});

export const DateTimeSchema = z.object({
  type: z.literal(SchemaFieldType.DATE_TIME),
  required: z.boolean()
});

export const ConnectResourceSchema = z.object({
  type: z.literal(SchemaFieldType.CONNECT_RESOURCE),
  resourceType: z.nativeEnum(ConnectResourceType),
  required: z.boolean()
});

export const PropertySchema = z.discriminatedUnion('type', [
  StringSchema,
  NumberSchema,
  BooleanSchema,
  PromptSchema,
  YamlSchema,
  PickListSchema,
  ReferenceSchema,
  ObjectSchema,
  DateTimeSchema,
  ConnectResourceSchema
]);

export enum ConditionOperator {
  EQUALS = 'equals',
  IN = 'in'
}

export enum ConditionMode {
  AND = 'AND',
  OR = 'OR'
}

export const FieldConditionSchema = z.object({
  field: z.string().min(1),
  operator: z.enum(ConditionOperator),
  value: z.union([
    z.boolean(), // For boolean checks
    z.array(z.string()).min(1).max(25) // For picklist (single or multiple)
  ])
});

export const ConditionGroupSchema = z.object({
  mode: z.enum(ConditionMode),
  conditions: z.array(FieldConditionSchema).min(1).max(5)
});

export const FieldSchema = z.object({
  definition: PropertySchema,
  primary: z.boolean(),
  overridable: z.boolean(),
  hidden: z.boolean().default(false),
  presentable: z.boolean().default(true),
  inherited: z.boolean(),
  access: z.record(z.string(), z.array(z.string())),
  conditions: z
    .object({
      groups: z.array(ConditionGroupSchema).min(0).max(5)
    })
    .nullable()
    .default({
      groups: []
    }),
  order: z.number().int().min(0)
});

export type Property = z.infer<typeof PropertySchema>;
export type Field = z.infer<typeof FieldSchema>;
export type FieldCondition = z.infer<typeof FieldConditionSchema>;
export type ConditionGroup = z.infer<typeof ConditionGroupSchema>;

export const BaseSchemaSchema = z.object({
  // TODO: Add constraints to the field name (no spaces, special chars, etc.)
  fields: z.record(z.string(), FieldSchema).refine(validatePrimaryField, {
    message: 'There must be exactly one primary field which is required and must be a string.'
  })
});

export const ValueSchema = z.union([z.string(), z.number(), z.boolean(), z.looseObject({}), z.json()]);

export type Value = z.infer<typeof ValueSchema>;

export const DefaultValueSchema = z.object({
  value: ValueSchema.nullable(),
  modifiable: z.boolean(),
  overridable: z.boolean(),
  source: z.enum(['user', 'inherited'])
});

export const DefaultValuesSchema = z.record(z.string(), DefaultValueSchema);

export const Permissions = z.object({
  read: z.array(z.string()),
  write: z.array(z.string()),
  delete: z.array(z.string())
});

export type Permissions = z.infer<typeof Permissions>;
export type BaseSchema = z.infer<typeof BaseSchemaSchema>;
export type DefaultValue = z.infer<typeof DefaultValueSchema>;
export type DefaultValues = z.infer<typeof DefaultValuesSchema>;

export const CreateEntityPayloadSchema = z.object({
  name: z.string().min(2).max(100),
  parentId: z.string(),
  description: z.string().max(500)
});

export type CreateEntityPayload = z.infer<typeof CreateEntityPayloadSchema>;

export const UpdateEntityPayloadSchema = z.object({
  name: z.string().min(2).max(100).optional(),
  description: z.string().max(500).optional(),
  schema: z
    .object({
      fields: z.record(z.string(), FieldSchema.omit({ inherited: true })).refine(validatePrimaryField, {
        message: 'There must be exactly one primary field which is required and must be a string.'
      })
    })
    .optional(),
  defaults: z
    .record(
      z.string(),
      z.object({
        value: DefaultValueSchema.shape.value,
        overridable: DefaultValueSchema.shape.overridable,
        source: DefaultValueSchema.shape.source
      })
    )
    .refine(
      (data) => {
        for (const [_, value] of Object.entries(data)) {
          if (value.source === 'inherited' && value.value) {
            return false;
          }

          if (value.source === 'user' && value.value === undefined) {
            return false;
          }
        }
        return true;
      },
      {
        message: "Defaults with source 'inherited' must not have a value provided."
      }
    )
    .optional(),
  permissions: Permissions.optional()
});

export const UpdateEntityPathParametersSchema = z.object({
  entityId: z.string()
});

export const GetSuggestionsPayloadSchema = z.object({
  namePrefix: z.string().min(2).max(25).optional(),
  type: z.enum(['CONTACT_FLOW', 'CONTACT_FLOW_MODULE', 'PROMPT', 'PHONE_NUMBER', 'QUEUE'])
});

export type UpdateEntityPayload = z.infer<typeof UpdateEntityPayloadSchema>;
export type UpdateEntityPathParameters = z.infer<typeof UpdateEntityPathParametersSchema>;
export type GetSuggestionsPayload = z.infer<typeof GetSuggestionsPayloadSchema>;
